import type { CoverageAreaInput } from "./coverageLookup.js";
import type { IssueCounts } from "./coverageNotes.js";
import { pointInCircle, pointInPolygon, ringsFromGeoJson } from "./pointInArea.js";
import type { LatLng } from "../maps/types.js";

export type SaleDot = {
  id: string;
  lat: number;
  lng: number;
  soldAt: string;
  amountUsd: number | null;
  label: string;
};

export type SaleIssue = {
  id: string;
  saleFactId: string | null;
  partyId: string | null;
  kind: "customer" | "delivery" | "installation";
  note: string;
  createdAt: string;
};

export type CoverageQuote = {
  id: string;
  addressText: string;
  lat: number;
  lng: number;
  precision: "rooftop" | "city";
  program: string;
  sizeLabel: string;
  suggestedPartyId: string | null;
  chosenPartyId: string | null;
  priceUsd: number | null;
  leadTime: string;
  reason: string;
  status: "draft" | "finalized";
  dealId: string | null;
};

export type FadeMode = "age" | "dollars";
export type IssueFilter = "all" | SaleIssue["kind"];

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

export function saleOpacity(dot: SaleDot, mode: FadeMode, nowMs: number, peers: SaleDot[]): number {
  if (mode === "dollars") {
    const max = Math.max(1, ...peers.map((peer) => peer.amountUsd ?? 0));
    return roundOpacity(0.2 + 0.7 * ((dot.amountUsd ?? 0) / max));
  }
  const age = nowMs - Date.parse(dateStart(dot.soldAt));
  if (!Number.isFinite(age) || age <= 0) return 0.9;
  if (age >= YEAR_MS) return 0.15;
  return roundOpacity(0.9 - (age / YEAR_MS) * 0.75);
}

export function filterSaleDots(dots: SaleDot[], issues: SaleIssue[], kind: IssueFilter): SaleDot[] {
  if (kind === "all") return dots;
  const ids = new Set(issues.filter((issue) => issue.kind === kind && issue.saleFactId).map((issue) => issue.saleFactId));
  return dots.filter((dot) => ids.has(dot.id));
}

export function recentIssueCounts(args: {
  partyId: string;
  areas: CoverageAreaInput[];
  sales: SaleDot[];
  issues: SaleIssue[];
  nowMs: number;
  quietDays: number;
}): IssueCounts {
  const counts: IssueCounts = { delivery: 0, installation: 0, customer: 0 };
  const bySale = new Map(args.sales.map((sale) => [sale.id, sale]));
  const windowStart = args.nowMs - args.quietDays * DAY_MS;
  for (const issue of args.issues) {
    const at = Date.parse(issue.createdAt);
    if (!Number.isFinite(at) || at < windowStart || at > args.nowMs) continue;
    if (issue.partyId && issue.partyId !== args.partyId) continue;
    if (!issue.partyId) {
      const sale = issue.saleFactId ? bySale.get(issue.saleFactId) : undefined;
      if (!sale || !insideFull({ lat: sale.lat, lng: sale.lng }, args.partyId, args.areas)) continue;
    }
    counts[issue.kind] += 1;
  }
  return counts;
}

export function saleDotFeatures(dots: SaleDot[], mode: FadeMode, nowMs: number) {
  return {
    type: "FeatureCollection" as const,
    features: dots.map((dot) => ({
      type: "Feature" as const,
      properties: { title: dot.label, opacity: saleOpacity(dot, mode, nowMs, dots) },
      geometry: { type: "Point" as const, coordinates: [dot.lng, dot.lat] },
    })),
  };
}

function insideFull(point: LatLng, partyId: string, areas: CoverageAreaInput[]): boolean {
  return areas.some((area) => area.partyId === partyId && area.tier === "full" && contains(point, area));
}

function contains(point: LatLng, area: CoverageAreaInput): boolean {
  if (area.kind === "circle") {
    if (area.centerLat == null || area.centerLng == null || area.radiusKm == null) return false;
    return pointInCircle(point, { lat: area.centerLat, lng: area.centerLng }, area.radiusKm);
  }
  const rings = ringsFromGeoJson(area.geojson);
  return rings ? pointInPolygon(point, rings) : false;
}

function dateStart(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00.000Z` : value;
}

function roundOpacity(value: number): number {
  return Math.round(value * 100) / 100;
}
